import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatApiError, tokenStore } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ResendVerificationButton } from "@/components/ResendVerificationButton";

// Landing voor `/verify-email?token=…`: activeert het account en logt direct in.
export default function VerifyEmailPage() {
  const [search] = useSearchParams();
  const token = search.get("token");
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setStatus("failed"); setMessage("Geen token in de link."); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data.token) tokenStore.set(data.token);
        setUser(data);
        setStatus("success");
        setTimeout(() => nav("/"), 1800);
      } catch (e) {
        // Link al gebruikt maar sessie actief (bv. pagina ververst) → toon gewoon succes.
        try {
          const me = await api.get("/auth/me");
          if (me?.data?.id) { setUser(me.data); setStatus("success"); setTimeout(() => nav("/"), 1800); return; }
        } catch { /* niet ingelogd */ }
        setMessage(formatApiError(e.response?.data?.detail) || "Bevestigen mislukt.");
        setStatus("failed");
      }
    })();
  }, [token, nav, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background grid-bg">
      <div className="w-full max-w-md bg-card border-2 border-foreground p-8 heavy-shadow text-center" data-testid="verify-email-page">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <h1 className="font-display font-black text-2xl uppercase mt-4">Even bevestigen…</h1>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-positive" data-testid="verify-success" />
            <div className="stat-label mt-3 text-positive">Bevestigd</div>
            <h1 className="font-display font-black text-2xl uppercase mt-1">Je account is actief</h1>
            <p className="text-sm text-muted-foreground mt-3">Je bent ingelogd — we brengen je naar je dashboard.</p>
            <Link to="/" data-testid="verify-success-dashboard" className="mt-5 inline-block btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm">Naar het dashboard</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" data-testid="verify-failed" />
            <h1 className="font-display font-black text-2xl uppercase mt-4">Bevestigen mislukt</h1>
            <p className="text-sm text-muted-foreground mt-3" data-testid="verify-failed-message">{message}</p>
            <div className="mt-5 space-y-3">
              <ResendVerificationButton />
              <div><Link to="/login" data-testid="verify-failed-login" className="text-sm underline underline-offset-2">Naar inloggen</Link></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
